<template>
  <page content-class="admin-campaign-list">
    <campaign-editor :campaign="campaign" step="engagement">

      <div class="d-flex justify-content-center align-items-middle my-5" v-if="!campaign">
        <b-spinner variant="primary"/>
      </div>

      <b-row v-else class="stages-editor m-0 h-100">

        <!-- LEFT SIDE -->
        <div class="border-right p-0 left-side">
          <div class="d-flex justify-content-center align-items-center border-bottom start-box">
            <span>{{ campaign.startDate | formatDate('Do MMM, HH:mm') }}</span>
          </div>
          <div class="scrollable-box">
            <div class="scrollable-box-loading d-flex justify-content-center align-items-center" v-if="areDatesUpdating">
              <b-spinner type="grow" variant="primary"/>
            </div>
            <div class="d-flex flex-column justify-content-start align-items-center flex-fill py-2 px-2 stages-box">
              <b-button
                v-if="!campaign.isDisabled"
                class="mt-2 mb-3 p-1"
                variant="light"
                pill
                size="sm"
                block
                @click="openNewComponentModal(null)"
              >
                <span style="font-size: 16px; font-weight: 500">+</span>
              </b-button>
              <draggable
                v-if="Array.isArray(orderedComponents) && orderedComponents.length"
                :disabled="disableDraggable"
                handle=".drag-handle"
                v-model="draggableComponents"
                @end="reorderComponents"
              >
                <div
                  v-for="component in draggableComponents"
                  :key="component.id"
                  class="w-100 drag-handle"
                >
                  <b-card
                    v-if="componentInfo(component).relativeSendAt"
                    class="text-center mb-2 active-on-hover"
                    bodyClass="p-2"
                    :style="{ borderRadius: '4px', cursor: 'pointer' }"
                    @click="openEditDaysModal(component)"
                  >
                    {{ componentInfo(component).relativeSendAt.days }} {{ componentInfo(component).relativeSendAt.days > 1 ? 'days' : 'day' }}
                  </b-card>
                  <b-card
                    class="w-100 mb-2 cursor-pointer"
                    :bodyClass="{'p-3': true, 'active-on-hover': !activeComponent || component.id !== activeComponent.id }"
                    @click="changeComponent(component)"
                    :style="{
                      background: activeComponent && component.id === activeComponent.id ? '#f1f5f9' : '#fff',
                      borderRadius: '4px',
                    }"
                  >
                    <div class="d-flex justify-content-start align-items-center w-100">
                      <span class="component-icon" :style="{
                        background: componentInfo(component).key === 'email' ? '#06b6d4' : componentInfo(component).key === 'slack' ? '#ec4899' : componentInfo(component).key === 'push' ? '#000000' : '#f97316',
                        fontSize: '10px'
                      }">
                        <font-awesome-icon :icon="componentInfo(component).icon" />
                      </span>
                      <div class="flex-fill">
                        <div class="d-flex justify-content-between align-items-center w-100">
                          <div style="font-weight: 500; width: 120px; text-overflow: ellipsis;">{{ componentInfo(component).label }}</div>
                          <div
                            v-if="componentInfo(component).status"
                            :style="{
                              background: componentInfo(component).status.color,
                              color: '#fff',
                              fontWeight: 500,
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '4px'
                            }"
                          >{{ componentInfo(component).status.label}}</div>
                        </div>
                        <div class="w-100">
                          <template v-if="['email', 'push', 'slack', 'teams'].includes(componentInfo(component).key)">
                            <span style="font-size: 12px" :class="{ 'text-muted': !(activeComponent && component.id === activeComponent.id) }">
                              <template v-if="componentInfo(component).startDate">
                                {{componentInfo(component).startDate | formatDate('dddd, Do MMM, HH:mm')}}
                              </template>
                              <template v-else>—</template>
                            </span>
                          </template>
                          <template v-else>
                            <span style="font-size: 12px" :class="{ 'text-muted': !(activeComponent && component.id === activeComponent.id) }">
                              <template v-if="componentInfo(component).startDate">
                                {{componentInfo(component).startDate | formatDate('dddd, Do MMM, HH:mm')}}
                              </template>
                              <template v-else>—</template>
                            </span>
                            <br><span style="font-size: 12px" :class="{ 'text-muted': !(activeComponent && component.id === activeComponent.id) }">
                              <template v-if="componentInfo(component).endDate">
                                - {{componentInfo(component).endDate | formatDate('dddd, Do MMM, HH:mm')}}
                              </template>
                              <template v-else>—</template>
                            </span>
                          </template>
                        </div>
                      </div>
                    </div>
                  </b-card>
                  <b-button
                    v-if="!campaign.isDisabled"
                    class="my-3 p-1"
                    variant="light"
                    pill
                    size="sm"
                    block
                    @click="openNewComponentModal(component.id)"
                  >
                    <span style="font-size: 16px; font-weight: 500">+</span>
                  </b-button>
                  <!-- edit days modal -->
                  <b-modal
                    :id="`edit-days-modal-${component.id}`"
                    title="Edit Days"
                    size="sm"
                    hide-footer
                    lazy
                  >
                    <b-row class="border-bottom pb-3">
                      <b-col cols="12">
                        <div class="d-flex justify-content-start align-items-center w-100">
                          <div class="flex-fill">
                            <b-form-input v-model="editDaysForm.days" type="number" />
                          </div>
                          <div class="ml-3">days</div>
                        </div>
                      </b-col>
                      <b-col v-if="!isLastComponent(component.id)" cols="12" class="pt-3">
                        <b-form-checkbox v-model="editDaysForm.moveComponentsBack" name="check-button" switch>
                          Move all the following messages by the same amount of time
                        </b-form-checkbox>
                      </b-col>
                      <!-- @Todo: make componentsMustMove to be a method -->
                      <b-col v-if="componentsMustMoveMethod(component.id, editDaysForm.days, editDaysForm.moveComponentsBack)" cols="12" class="pt-3">
                        <b-alert variant="danger" show>
                          The selected time will overlap with the next message so messages must be moved back. Please change the amount of days or toggle the switch above.
                        </b-alert>
                      </b-col>
                    </b-row>
                    <b-row class="mt-3">
                      <b-col cols="12" class="pt-3">
                        <b-button
                          variant="light"
                          class="px-4"
                          pill
                          block
                          @click="updateComponentDays(component.id)"
                        >Save</b-button>
                      </b-col>
                    </b-row>
                  </b-modal>
                </div>
              </draggable>
            </div>
          </div>
          <div class="d-flex justify-content-center align-items-center p-3 border-top end-box">
            <span>{{ campaign.endDate | formatDate('Do MMM, HH:mm') }}</span>
          </div>
        </div>

        <!-- RIGHT SIDE -->
        <div class="p-0 d-flex flex-column justify-content-between right-side">

            <!-- COMPONENT -->
            <template v-if="!activeComponent">
              <b-col md="12" class="p-5 d-flex flex-column justify-content-center align-items-center">
                <span style="font-size: 18px; font-weight: 500;" class="text-muted">Select a message to edit, or create a new one</span>
                <b-button
                  variant="light"
                  class="mt-3 px-4"
                  @click="openNewComponentModal(null)"
                  pill
                >Add New Message</b-button>
              </b-col>
            </template>
            <template v-if="activeComponent === 'loading'">
              <b-col md="12" class="p-5 d-flex justify-content-center align-items-middle">
                <b-spinner variant="primary" label="Loading…"></b-spinner>
              </b-col>
            </template>
            <template v-else-if="componentInfo(activeComponent).key === 'email'">
              <update-email-message
                :components="orderedComponents"
                :campaign="campaign"
                :campaignStartDate="typeof campaign.startDate === 'number' || typeof campaign.startDate === 'string' ?
                  new Date(campaign.startDate) : campaign.startDate"
                :campaignID="campaignID"
                :campaignComponentID="activeComponent.id"
                :collectiveGoal="collectiveGoal"
                :value="activeComponent"
                :syncedEmailAddresses="syncedEmailAccounts"
                :viewOnly="campaign.isDisabled"
                @input="updateComponent($event)"
                @delete="({ moveComponents }) => deleteComponent(activeComponent, moveComponents)"
              />
            </template>
            <template v-else-if="componentInfo(activeComponent).key === 'slack'">
              <update-slack-message
                :components="orderedComponents"
                :campaign="campaign"
                :campaignStartDate="typeof campaign.startDate === 'number' || typeof campaign.startDate === 'string' ?
                  new Date(campaign.startDate) : campaign.startDate"
                :campaignID="campaignID"
                :campaignComponentID="activeComponent.id"
                :collectiveGoal="collectiveGoal"
                :value="activeComponent"
                :viewOnly="campaign.isDisabled"
                :syncedEmailAddresses="(syncedEmailAddresses || {}).syncedSlackEmails"
                @input="updateComponent($event)"
                @delete="({ moveComponents }) => deleteComponent(activeComponent, moveComponents)"
              />
            </template>
            <template v-else-if="componentInfo(activeComponent).key === 'push'">
              <update-push-message
                :components="orderedComponents"
                :campaign="campaign"
                :campaignStartDate="typeof campaign.startDate === 'number' || typeof campaign.startDate === 'string' ? new Date(campaign.startDate) : campaign.startDate"
                :campaignID="campaignID"
                :campaignComponentID="activeComponent.id"
                :collectiveGoal="collectiveGoal"
                :value="activeComponent"
                :viewOnly="campaign.isDisabled"
                @input="updateComponent($event)"
                @delete="({ moveComponents }) => deleteComponent(activeComponent, moveComponents)"
              />
            </template>
            <template v-else-if="componentInfo(activeComponent).key === 'teams'">
              <update-teams-message
                :components="orderedComponents"
                :campaign="campaign"
                :campaignStartDate="typeof campaign.startDate === 'number' || typeof campaign.startDate === 'string' ?
                  new Date(campaign.startDate) : campaign.startDate"
                :campaignID="campaignID"
                :campaignComponentID="activeComponent.id"
                :collectiveGoal="collectiveGoal"
                :value="activeComponent"
                :syncedEmailAddresses="(syncedEmailAddresses || {}).syncedMicrosoftEmails"
                :viewOnly="campaign.isDisabled"
                @input="updateComponent($event)"
                @delete="({ moveComponents }) => deleteComponent(activeComponent, moveComponents)"
              />
            </template>
            <template v-else-if="componentInfo(activeComponent).key === 'referral-jam'">
              <b-col md="12" class="pl-3 pr-2 py-4">
                <update-referral-jam :eventID="activeComponent.event.id" :campaignID="campaignID" :jobs="campaign.jobs" />
              </b-col>
            </template>
        </div>
      </b-row>
    </campaign-editor>

    <b-modal
      id="new-component-modal"
      title="New Message"
      size="lg"
      hide-footer
      lazy
    >
    <b-row class="border-bottom pb-3">
      <b-col cols="12">
        <h4><strong>Schedule</strong></h4>
      </b-col>
      <b-col cols="12">
        <div class="d-flex justify-content-start align-items-center w-100">
          <!-- Continue here making sure that relativeSendAtForm.days can not be more than the same value for the
          next comopnent if moveBack is true -->
          <div style="width: 80px">
            <b-form-input v-model="relativeSendAtForm.days" type="number" />
          </div>
          <div class="ml-3">
            <strong>{{ relativeSendAtForm.days > 1 ? 'days' : 'day' }} after the {{ parentComponentID ? 'previous message' : 'start of the campaign' }} at</strong>
          </div>
          <div style="width: 80px" class="ml-3">
            <b-form-select v-model="relativeSendAtForm.atHour" :options="hourOptions" />
          </div>
          <div class="ml-2">:</div>
          <div style="width: 80px" class="ml-2">
            <b-form-select v-model="relativeSendAtForm.atMinute" :options="minuteOptions" />
          </div>
        </div>
      </b-col>
      <b-col v-if="!isLastComponent(parentComponentID)" cols="12" class="pt-3">
        <b-form-checkbox v-model="moveComponentsBack" name="check-button" switch>
          Move back all the following messages by the same amount of time
        </b-form-checkbox>
      </b-col>
      <b-col v-if="componentsMustMove" cols="12" class="pt-3">
        <b-alert variant="danger" show>
          The selected time will overlap with the next message so messages must be moved back. Please change the amount of days or toggle the switch above.
        </b-alert>
      </b-col>
    </b-row>
    <b-row class="mt-3">
      <b-col cols="12">
        <h4><strong>Select a message type</strong></h4>
      </b-col>
      <b-col v-for="({ key, label, icon, displayOptions, options }, i) in allowedComponents" :key="key" cols="12" class="pt-3">
        <b-card no-body class="mb-1">
          <b-card-header class="d-flex justify-content-between align-items-center">
            <div @click="displayOptions = !displayOptions" v-b-toggle="`component-${key}`">
              <h4><strong><span class="mr-3 text-muted"><font-awesome-icon :icon="icon" /></span>{{ label }}</strong></h4>
            </div>
            <b-button @click="allowedComponents[i].displayOptions = !displayOptions"  v-b-toggle="`component-${key}`" variant="light" class="px-2" size="sm">
              <font-awesome-icon v-if="displayOptions" icon="chevron-up" size="sm"/>
              <font-awesome-icon v-else icon="chevron-down" size="sm"/>
            </b-button>
          </b-card-header>
          <b-collapse :id="`component-${key}`" :accordion="`component-${key}`" role="tabpanel">
            <div v-for="{ id, title, body, description, type } in options" :key="id" class="border-bottom d-flex justify-content-between align-items-center p-3">
              <div>
                <span style="font-size: 15px"><strong>{{ title }}</strong> ({{ type }})</span><br>
                <span class="text-muted">{{ description }}</span>
              </div>
              <div style="width: 160px" class="d-flex justify-content-end align-items-center">
                <b-button
                  :disabled="componentsMustMove || isComponentBeingAdded"
                  size="sm"
                  class="px-3"
                  variant="light"
                  @click="createComponent(key, id)"
                  pill
                >Select</b-button>
              </div>
            </div>
          </b-collapse>
        </b-card>
      </b-col>
    </b-row>
    </b-modal>
  </page>
</template>

<script>
import _isEqual from 'lodash/isEqual';
import gql from 'graphql-tag';
import { Twitter as ColourPicker } from 'vue-color';
import emailTemplates from './templates/emails';
import pushTemplates from './templates/push';
import slackTemplates from './templates/slack';
import teamsTemplates from './templates/teams';
import CampaignAdminStagesQuery from '@/graphql/queries/CampaignAdminStages.gql';
import CreateCampaignComponentMutation from '@/graphql/mutations/CreateCampaignComponent.gql';
import UpdateCampaignComponentMutation from '@/graphql/mutations/UpdateCampaignComponent.gql';
import Page from '@/components/campaign-admin/page';
import formatDate from 'date-fns/format';
import CampaignEditor from '@/components/campaign-admin/campaign-editor-frame';
import UpdateEmailMessage from '@/components/campaign-admin/update-email-message-relative-dates';
import UpdatePushMessage from '@/components/campaign-admin/update-push-message-relative-dates';
import UpdateSlackMessage from '@/components/campaign-admin/update-slack-message-relative-dates';
import UpdateTeamsMessage from '@/components/campaign-admin/update-teams-message-relative-dates';
import UpdateReferralJam from '@/components/campaign-admin/update-referral-jam';
import Draggable from 'vuedraggable';
import { mapRoot } from '@src/mixins/root';

const CreateEventMutation = gql`
  mutation CreateEvent($create: CreateEventInput!) {
    event: createEvent(create: $create) {
      id
    }
  }
`;

const DeleteCampaignComponentMutation = gql`
  mutation CreateEvent($id: ID!, $campaignID: ID!) {
    deleteCampaignComponent(id: $id, campaign: { id: $campaignID })
  }
`;

const SyncedEmailAddressesQuery = gql`
  query SyncedEmailAddresses($userID: ID!) {
    company {
      id
      syncedSlackEmails: users(
        where: {
          id: $userID,
          NOT: { AND: [ { state: null }, { state: { slack: null } } ] }
        }
      ) {
          id
          emailAddress
          fullName
      }
      syncedMicrosoftEmails: users(
        where: {
          NOT: { AND: [ { state: null }, { state: { microsoft: null } } ] }
        }
      ) {
          id
          emailAddress
          fullName
      }
      syncedGmailEmails: users(
        where: {
          NOT: { AND: [ { state: null }, { state: { gmail: null } } ] }
        }
      ) {
          id
          emailAddress
          fullName
      }
    }
  }
`;

const allowedComponents = (templateFolder, { hasSlackEnabled, hasMicrosoftEnabled, hasGoogleEnabled, hasPushEnabled }) => ([ {
  key: 'email',
  label: 'Scheduled Emails',
  icon: 'envelope',
  status: hasGoogleEnabled || hasMicrosoftEnabled ? 'ACTIVE' : 'INACTIVE',
  displayOptions: false,
  options: emailTemplates(templateFolder).filter(({ id }) => {
    const idsToExclude = [ 'email_promote_a_job', 'email_promote_content' ];
    return !idsToExclude.includes(id);
  })
}, {
  key: 'slack',
  label: 'Scheduled Slack Messages',
  icon: [ 'fab', 'slack' ],
  status: hasSlackEnabled ? 'ACTIVE' : 'INACTIVE',
  displayOptions: false,
  options: slackTemplates(templateFolder).filter(({ id }) => {
    const idsToExclude = [ 'slack_promote_a_job', 'slack_promote_content' ];
    return !idsToExclude.includes(id);
  })
}, {
  key: 'push',
  label: 'Scheduled Push Notifications',
  icon: [ 'fa', 'mobile' ],
  status: hasPushEnabled ? 'ACTIVE' : 'INACTIVE',
  displayOptions: false,
  options: pushTemplates,
}, {
  key: 'teams',
  label: 'Scheduled Teams Messages',
  icon: [ 'fab', 'windows' ],
  status: hasMicrosoftEnabled ? 'ACTIVE' : 'INACTIVE',
  displayOptions: false,
  options: teamsTemplates(templateFolder).filter(({ id }) => {
    const idsToExclude = [ 'teams_promote_a_job', 'teams_promote_content' ];
    return !idsToExclude.includes(id);
  })
}, {
  key: 'referral-jam',
  label: 'Events',
  icon: 'calendar',
  status: 'ACTIVE',
  displayOptions: false,
  options: [ {
    id: 'event_referral_jam',
    title: 'Referral Jam',
    type: 'Group',
    description: 'A short usually 30 minutes live event to boost referrals',
  } ]
} ]);

export default {
  name: 'CampaignAdminStages',
  components: {
    Page,
    CampaignEditor,
    ColourPicker,
    UpdateReferralJam,
    UpdateEmailMessage,
    UpdatePushMessage,
    UpdateSlackMessage,
    UpdateTeamsMessage,
    Draggable
  },
  props: {
    campaignID: {
      type: String,
    },
  },
  data: () => ({
    activeComponent: null,
    moveComponentsBack: true,
    disableDraggable: false,
    draggableComponents: [],
    areDatesUpdating: false,
    isComponentBeingAdded: false,
    editDaysForm: {
      days: 0,
      moveComponentsBack: true,
    },
    relativeSendAtForm: {
      days: '3',
      atHour: 12,
      atMinute: 30,
    },
    parentComponentID: null,
  }),
  apollo: {
    campaign: {
      query: CampaignAdminStagesQuery,
      variables() {
        const { campaignID } = this;
        return { campaignID, withJobs: true };
      },
      update({ company }) {
        const { campaign } = company;
        return campaign;
      },
      error(err) {
        this.$notifyFatalErr(err, { prepend: 'Failed to load the campaigns' });
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    },
    syncedEmailAddresses: {
      query: SyncedEmailAddressesQuery,
      variables() {
        const { user } = this;
        const { id: userID } = user || {};
        return { userID };
      },
      update({ company }) {
        const { syncedSlackEmails, syncedMicrosoftEmails, syncedGmailEmails } = company || {};
        return { syncedSlackEmails, syncedMicrosoftEmails, syncedGmailEmails };
      },
      error(err) {
        this.$notifyFatalErr(err, { prepend: 'Failed to load the synced email addresses' });
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    },
  },
  computed: {
    ...mapRoot([ 'user', 'company', 'isDemoSite', 'isInMobi', 'isLNW', 'isPicnic', 'isApplusIdiada' ]),
    componentsMustMove() {
      const { orderedComponents, parentComponentID, relativeSendAtForm, moveComponentsBack } = this;
      if (moveComponentsBack) return false;
      if (!Array.isArray(orderedComponents) || !orderedComponents.length) return false;
      if (!parentComponentID) return false;
      const parentIndex = orderedComponents.findIndex(({ id }) => id === parentComponentID);
      const days = parseInt(relativeSendAtForm.days);
      const nextComponent = orderedComponents[parentIndex + 1];
      if (!nextComponent) return false;
      const nextComponentMessage = nextComponent.emailMessage || nextComponent.slackMessage || nextComponent.teamsMessage || nextComponent.pushMessage;
      return nextComponentMessage.relativeSendAt.days < days;
    },
    hourOptions() {
      return Array.from({ length: 24 }, (_, i) => i).map(i => ({ value: i, text: i.toString().padStart(2, '0') }));
    },
    minuteOptions() {
      return [ 0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55 ].map(i => ({ value: i, text: i.toString().padStart(2, '0') }));
    },
    sitePreferences() {
      const { sitePreferences } = this.company || {};
      return sitePreferences || {};
    },
    hasGoogleEnabled() {
      const { commsChannelsEnabled } = this.sitePreferences || {};
      return (commsChannelsEnabled || []).includes('google');
    },
    hasPushEnabled() {
      const { commsChannelsEnabled } = this.sitePreferences || {};
      return (commsChannelsEnabled || []).includes('push');
    },
    hasSlackEnabled() {
      const { commsChannelsEnabled } = this.sitePreferences || {};
      return (commsChannelsEnabled || []).includes('slack');
    },
    hasMicrosoftEnabled() {
      const { commsChannelsEnabled } = this.sitePreferences || {};
      return (commsChannelsEnabled || []).includes('microsoft');
    },
    allowedComponents() {
      const { isInMobi, isLNW, hasGoogleEnabled, hasMicrosoftEnabled, hasPushEnabled, hasSlackEnabled, isDemoSite, isPicnic, isApplusIdiada } = this;

      let templateFolder = null;
      if (isInMobi) {
        templateFolder = 'inMobi';
      }
      if (isLNW) {
        templateFolder = 'lnw';
      }
      if (isDemoSite) {
        templateFolder = 'demo';
      }
      if (isPicnic) {
        templateFolder = 'picnic';
      }
      if (isApplusIdiada) {
        templateFolder = 'applus';
      }

      const components = allowedComponents(templateFolder, { hasGoogleEnabled, hasMicrosoftEnabled, hasPushEnabled, hasSlackEnabled });

      return components.filter(component => component.status === 'ACTIVE');
    },
    formatDate: () => formatDate,
    components() {
      const { campaign } = this;
      if (!campaign) return [];
      return campaign.components;
    },
    orderedComponents() {
      const { components } = this;
      const orderedComponents = [];
      const validComponents = (components || [])
        .filter(({ pushMessage, slackMessage, teamsMessage, emailMessage, event }) => pushMessage || slackMessage || teamsMessage || emailMessage || event);
      const findNext = componentID => {
        const currentComponent = validComponents.find(({ pushMessage, teamsMessage, slackMessage, emailMessage }) => {
          const { relativeSendAt: { componentID: parentComponentID } } = pushMessage || teamsMessage || slackMessage || emailMessage;
          return parentComponentID === componentID;
        });
        if (currentComponent) {
          orderedComponents.push(currentComponent);
          findNext(currentComponent.id);
        }
      };
      findNext(null);
      return orderedComponents;
    },
    branding() {
      const { campaign } = this;
      if (!campaign) return {};
      return campaign.branding;
    },
    collectiveGoal() {
      const { components } = this;
      if (!components) return null;
      const collectiveGoal = components.find(({ collectiveGoal }) => collectiveGoal);
      return collectiveGoal ? collectiveGoal.collectiveGoal : null;
    },
    syncedEmailAccounts() {
      const { syncedEmailAddresses } = this;
      const { syncedMicrosoftEmails, syncedGmailEmails } = syncedEmailAddresses || {};
      if (Array.isArray(syncedMicrosoftEmails) && syncedMicrosoftEmails.length && Array.isArray(syncedGmailEmails) && syncedGmailEmails.length) {
        const mergedEmails = [ ...syncedMicrosoftEmails, ...syncedGmailEmails ];
        const uniqueIds = [];
        return mergedEmails.filter(({ id }) => {
          if (!uniqueIds.includes(id)) {
            uniqueIds.push(id);
            return true;
          }
          return false;
        });
      }
      if (Array.isArray(syncedMicrosoftEmails) && syncedMicrosoftEmails.length) {
        return syncedMicrosoftEmails;
      }
      if (Array.isArray(syncedGmailEmails) && syncedGmailEmails.length) {
        return syncedGmailEmails;
      }
      return null;
    },
    componentStatusMap() {
      return {
        DRAFT: {
          label: 'Draft',
          color: '#0ea5e9'
        },
        READY: {
          label: 'Ready',
          color: '#06b6d4'
        },
        SCHEDULED: {
          label: 'Scheduled',
          color: '#0ea5e9'
        },
        SCHEDULING: {
          label: 'Scheduling',
          color: '#0ea5e9'
        },
        ONHOLD: {
          label: 'On Hold',
          color: '#64748b'
        },
        DELIVERED: {
          label: 'Delivered',
          color: '#10b981'
        },
        ERRORED: {
          label: 'Errored',
          color: '#ef4444'
        },
      };
    },
  },
  watch: {
    orderedComponents: {
      handler() {
        this.draggableComponents = JSON.parse(JSON.stringify(this.orderedComponents));
      },
      immediate: true,
    },
  },
  methods: {
    openEditDaysModal(component) {
      if (this.campaign.isDisabled) return;
      const message = component.emailMessage || component.slackMessage || component.teamsMessage || component.pushMessage;
      this.editDaysForm = {
        days: message.relativeSendAt.days,
        moveComponentsBack: true,
      };
      this.$bvModal.show(`edit-days-modal-${component.id}`);
    },
    isLastComponent(componentID) {
      const { orderedComponents } = this;
      if (!Array.isArray(orderedComponents) || !orderedComponents.length) return true;
      return orderedComponents[orderedComponents.length - 1].id === componentID;
    },
    changeComponent(component) {
      if (typeof component === 'string') {
        component = this.orderedComponents.find(({ id }) => id === component);
      }
      this.activeComponent = 'loading';
      setTimeout(() => {
        this.activeComponent = component;
      }, 500);
    },
    openNewComponentModal(componentID) {
      this.parentComponentID = componentID;
      this.$bvModal.show('new-component-modal');
    },
    async createComponent(key, optionID) {
      const component = this.allowedComponents && this.allowedComponents.find(({ key: k }) => k === key);
      if (!component) return;
      const { campaignID: id, isInMobi, isLNW, isApplusIdiada, componentInfo, parentComponentID, campaign, moveComponentsBack, isDemoSite, isPicnic } = this;

      let templateFolder = null;
      if (isInMobi) {
        templateFolder = 'inMobi';
      }
      if (isLNW) {
        templateFolder = 'lnw';
      }
      if (isDemoSite) {
        templateFolder = 'demo';
      }
      if (isPicnic) {
        templateFolder = 'picnic';
      }
      if (isApplusIdiada) {
        templateFolder = 'applus';
      }

      const relativeSendAt = {
        days: parseInt(this.relativeSendAtForm.days),
        atHour: parseInt(this.relativeSendAtForm.atHour),
        atMinute: parseInt(this.relativeSendAtForm.atMinute),
        componentID: parentComponentID,
      };
      const oneDay = 24 * 60 * 60 * 1000;
      const parentComponent = parentComponentID ? this.orderedComponents.find(({ id }) => id === parentComponentID) : null;
      let prevSendAt = parentComponent ? componentInfo(parentComponent).startDate : null;
      prevSendAt = prevSendAt ? (prevSendAt instanceof Date ? prevSendAt : new Date(prevSendAt)) : null;
      const campaignStartDate = campaign.startDate instanceof Date ? campaign.startDate : new Date(campaign.startDate);
      prevSendAt = prevSendAt || campaignStartDate;
      prevSendAt.setHours(0);
      prevSendAt.setMinutes(0);
      const sendAt = new Date(prevSendAt.getTime() + relativeSendAt.days * oneDay + relativeSendAt.atHour * 60 * 60 * 1000 + relativeSendAt.atMinute * 60 * 1000);

      const create = {};
      if (key === 'email') {
        const { body, title, subject, inputs, id } = emailTemplates(templateFolder).find(({ id }) => id === optionID);
        create.emailMessage = {
          from: 'NONE',
          to: 'ALL_USERS',
          status: 'DRAFT',
          body: typeof body === 'string' ? body : (body ? JSON.stringify(body) : null),
          title,
          subject: subject || null,
          template: id,
          inputs: Object.keys(inputs || {}).reduce((r, key) => ({ ...r, [key]: inputs[key].defaultValue || null }), {}),
          relativeSendAt,
          sendAt,
        };
      } else if (key === 'slack') {
        const { body, title, inputs, id } = slackTemplates(templateFolder).find(({ id }) => id === optionID);
        create.slackMessage = {
          from: 'NONE',
          to: 'ALL_USERS',
          status: 'DRAFT',
          body: typeof body === 'string' ? body : (body ? JSON.stringify(body) : null),
          title,
          template: id,
          inputs: Object.keys(inputs || {}).reduce((r, key) => ({ ...r, [key]: inputs[key].defaultValue || null }), {}),
          relativeSendAt,
          sendAt,
        };
      } else if (key === 'teams') {
        const { body, title, inputs, id } = teamsTemplates(templateFolder).find(({ id }) => id === optionID);
        create.teamsMessage = {
          from: 'NONE',
          to: 'ALL_USERS',
          status: 'DRAFT',
          body: typeof body === 'string' ? body : (body ? JSON.stringify(body) : null),
          title,
          template: id,
          inputs: Object.keys(inputs || {}).reduce((r, key) => ({ ...r, [key]: inputs[key].defaultValue || null }), {}),
          relativeSendAt,
          sendAt,
        };
      } else if (key === 'push') {
        const { body, title, inputs, id } = pushTemplates.find(({ id }) => id === optionID);
        create.pushMessage = {
          to: 'ALL_USERS',
          status: 'DRAFT',
          body: typeof body === 'string' ? body : (body ? JSON.stringify(body) : null),
          title,
          template: id,
          inputs: Object.keys(inputs || {}).reduce((r, key) => ({ ...r, [key]: inputs[key].defaultValue || null }), {}),
          relativeSendAt,
          sendAt,
        };
      } else if (key === 'referral-jam') {
        const { components } = this;
        const { collectiveGoal, leaderboard } = (components || []).reduce((acc, { collectiveGoal, leaderboard }) => {
          if (collectiveGoal) acc.collectiveGoal = JSON.parse(JSON.stringify(collectiveGoal));
          if (leaderboard) {
            const { type, startDate, endDate, ...rest } = leaderboard;
            acc.leaderboard = JSON.parse(JSON.stringify(rest));
          }
          return acc;
        }, { collectiveGoal: null, leaderboard: null });
        if (collectiveGoal) delete this.removeTypeField(collectiveGoal);
        if (leaderboard) delete this.removeTypeField(leaderboard);

        const { data, errors } = await this.$apollo.mutate({
          mutation: CreateEventMutation,
          variables: {
            create: {
              title: 'Referral Jam',
              collectiveGoal,
              leaderboard,
              preferences: {
                canScheduleLinkedIn: true
              }
            },
          },
        });
        if (Array.isArray(errors)) {
          errors.forEach(err => this.$log.error('createEvent', err));
          throw errors.shift();
        }
        this.$assert(data && data.event && data.event.id, 404, 'Failed to create new event');
        const { event } = data;
        const { id: eventID } = event || {};
        create.event = {
          id: eventID,
        };
      }

      this.isComponentBeingAdded = true;
      try {
        const { campaignID } = this;
        const { data } = await this.$apollo.mutate({
          mutation: CreateCampaignComponentMutation,
          variables: {
            campaign: { id },
            create,
          },
          update(store, { data: { createCampaignComponent } }) {
            const { company } = store.readQuery({
              query: CampaignAdminStagesQuery,
              variables: { campaignID }
            });
            if (company.campaign && Array.isArray(company.campaign.components)) {
              company.campaign.components.push(createCampaignComponent);
            } else {
              company.campaign.components = [ createCampaignComponent ];
            }
            company.campaign.components.map(el => {
              if (el.emailMessage) {
                el.emailMessage.__typename = 'EmailMessage';
              }
              if (el.slackMessage) {
                el.slackMessage.__typename = 'SlackMessage';
              }
              if (el.teamsMessage) {
                el.teamsMessage.__typename = 'TeamsMessage';
              }
              if (el.pushMessage) {
                el.pushMessage.__typename = 'PushMessage';
              }
              return el;
            });
            store.writeQuery({
              query: CampaignAdminStagesQuery,
              variables: { campaignID },
              data: {
                company
              }
            });
          },
        });
        const { createCampaignComponent } = data || {};
        if (createCampaignComponent) {
          await this.insertComponent(createCampaignComponent, parentComponentID, moveComponentsBack);
          this.changeComponent(createCampaignComponent.id);
          this.$bvModal.hide('new-component-modal');
          this.$notification({ message: 'Component is successfully added', variant: 'success' });
        } else {
          this.$bvModal.hide('new-component-modal');
          this.$notifyErr('Failed to create component', { prepend: 'Failed to create component' });
        }
      } catch (error) {
        this.$notifyErr(error, { prepend: 'Failed to save component' });
        console.log(error);
      }
      this.isComponentBeingAdded = false;
    },
    async updateComponent(update) {
      const { campaignID: id, activeComponent } = this;
      try {
        await this.$apollo.mutate({
          mutation: UpdateCampaignComponentMutation,
          variables: {
            campaign: { id },
            componentID: activeComponent.id,
            update,
          },
        });
        this.$notification({ message: 'Component is successfully updated', variant: 'success' });
      } catch (error) {
        this.$notifyErr(error, { prepend: 'Failed to update component' });
        console.log(error);
      }
    },
    async deleteComponent(component, moveComponents) {
      try {
        const { id } = component;
        const { campaignID } = this;
        this.activeComponent = null;
        await this.removeComponent(id, moveComponents);
        await this.$apollo.mutate({
          mutation: DeleteCampaignComponentMutation,
          variables: { id, campaignID },
          update(store) {
            const { company } = store.readQuery({
              query: CampaignAdminStagesQuery,
              variables: { campaignID }
            });

            if (company.campaign && Array.isArray(company.campaign.components)) {
              const index = company.campaign.components.findIndex(({ id: componentID }) => id === componentID);
              company.campaign.components.splice(index, 1);
            }
            store.writeQuery({
              query: CampaignAdminStagesQuery,
              variables: { campaignID: id },
              data: {
                company
              }
            });
          },
        });
      } catch (error) {
        console.log(error);
        this.$notifyErr(error, { prepend: error.message || 'Failed to delete component' });
      }
    },
    componentInfo(component) {
      if (!component || typeof component === 'string') return {};
      const { event, emailMessage, slackMessage, teamsMessage, pushMessage } = component;
      const { parseDate } = this;
      const { status, sendAt, relativeSendAt } = pushMessage || emailMessage || slackMessage || teamsMessage || {};
      const isScheduledByStatus = [ 'SCHEDULED', 'SCHEDULING', 'ONHOLD', 'DELIVERED', 'ERRORED' ].includes(status);
      const now = new Date();
      const sendAtDate = typeof sendAt === 'string' || typeof sendAt === 'number' ? new Date(sendAt) : sendAt;
      const isLessThanFiveMinutes = sendAtDate && (sendAtDate.getTime() - now.getTime()) < 300000 && status === 'READY';
      const isScheduled = isScheduledByStatus || isLessThanFiveMinutes;
      if (event) {
        const component = this.allowedComponents.find(({ key: k }) => k === 'referral-jam');
        if (component) {
          const { key, label, icon } = component;
          const { startDate, endDate } = event;
          return { key, label: event.title ? event.title : label, icon, startDate: parseDate(startDate), endDate: parseDate(endDate) };
        } else {
          return {};
        }
      } else if (emailMessage) {
        const component = this.allowedComponents.find(({ key: k }) => k === 'email');
        if (component) {
          const { key, label, icon } = component;
          const { sendAt } = emailMessage;
          return { key, label: emailMessage.title ? emailMessage.title : label, icon, startDate: parseDate(sendAt), isScheduled, relativeSendAt, status: this.componentStatusMap[emailMessage.status] };
        } else {
          return {};
        }
      } else if (slackMessage) {
        const component = this.allowedComponents.find(({ key: k }) => k === 'slack');
        if (component) {
          const { key, label, icon } = component;
          const { sendAt } = slackMessage;
          return { key, label: slackMessage.title ? slackMessage.title : label, icon, startDate: parseDate(sendAt), isScheduled, relativeSendAt, status: this.componentStatusMap[slackMessage.status] };
        } else {
          return {};
        }
      } else if (pushMessage) {
        const component = this.allowedComponents.find(({ key: k }) => k === 'push');
        if (component) {
          const { key, label, icon } = component;
          const { sendAt } = pushMessage;
          return { key, label: pushMessage.title ? pushMessage.title : label, icon, startDate: parseDate(sendAt), isScheduled, relativeSendAt, status: this.componentStatusMap[pushMessage.status] };
        } else {
          return {};
        }
      } else if (teamsMessage) {
        const component = this.allowedComponents.find(({ key: k }) => k === 'teams');
        if (component) {
          const { key, label, icon } = component;
          const { sendAt } = teamsMessage;
          return { key, label: teamsMessage.title ? teamsMessage.title : label, icon, startDate: parseDate(sendAt), isScheduled, relativeSendAt, status: this.componentStatusMap[teamsMessage.status] };
        } else {
          return {};
        }
      }
      return {};
    },
    parseDate(dateString) {
      if (!dateString) return null;
      let date = Date.parse(dateString);
      if (isNaN(date)) {
        date = new Date(parseInt(dateString));
      }
      if (isNaN(date)) {
        date = new Date(dateString);
      }
      if (!isNaN(date)) {
        return date.getTime();
      }
      return null;
    },
    removeTypeField(obj) {
      const { removeTypeField } = this;
      if (!obj || typeof obj !== 'object') {
        return;
      }
      if (Array.isArray(obj)) {
        for (let i = 0; i < obj.length; i++) {
          removeTypeField(obj[i]);
        }
      } else {
        for (const prop in obj) {
          if (prop === '__typename' || prop === 'fileName' || prop === 'fileLocation' || prop === 'fileSize') {
            delete obj[prop];
          } else {
            removeTypeField(obj[prop]);
          }
        }
      }
    },
    async reorderComponents() {
      await this.updateComponentDates(this.draggableComponents);
    },
    componentsMustMoveMethod(componentID, daysString, moveComponentsBack) {
      const { orderedComponents } = this;
      if (moveComponentsBack) return false;
      if (!Array.isArray(orderedComponents) || !orderedComponents.length) return false;
      const currentIndex = orderedComponents.findIndex(({ id }) => id === componentID);
      const days = parseInt(daysString);
      const nextComponent = orderedComponents[currentIndex + 1];
      if (!nextComponent) return false;
      const nextComponentMessage = nextComponent.emailMessage || nextComponent.slackMessage || nextComponent.teamsMessage || nextComponent.pushMessage;
      return nextComponentMessage.relativeSendAt.days < days;
    },
    async insertComponent(component, parentComponentID, moveComponentsBack) {
      const components = JSON.parse(JSON.stringify(this.orderedComponents.filter(({ id }) => id !== component.id)));
      // insert component into components after the parent component, if parentComponentID is null, insert at the beginning
      const parentComponentIndex = components.findIndex(({ id }) => id === parentComponentID);
      if (parentComponentIndex === -1) {
        components.unshift(component);
      } else {
        components.splice(parentComponentIndex + 1, 0, component);
      }
      // update the days of the next components if moveComponentsBack is false
      const insertedComponentIndex = components.findIndex(({ id }) => id === component.id);
      const message = component.teamsMessage || component.slackMessage || component.emailMessage || component.pushMessage;
      const daysToShift = message.relativeSendAt.days;
      if (components[insertedComponentIndex + 1] && !moveComponentsBack) {
        const nextComponentMessageKey = components[insertedComponentIndex + 1].teamsMessage
          ? 'teamsMessage' : components[insertedComponentIndex + 1].slackMessage
            ? 'slackMessage' : components[insertedComponentIndex + 1].pushMessage
              ? 'pushMessage' : 'emailMessage';
        if (components[insertedComponentIndex + 1][nextComponentMessageKey].relativeSendAt.days >= daysToShift) {
          components[insertedComponentIndex + 1][nextComponentMessageKey].relativeSendAt.days -= daysToShift;
        } else {
          components[insertedComponentIndex + 1][nextComponentMessageKey].relativeSendAt.days = 0;
        }
      }
      await this.updateComponentDates(components);
    },
    async removeComponent(componentID, moveComponents) {
      const components = JSON.parse(JSON.stringify(this.orderedComponents));
      const componentIndex = components.findIndex(({ id }) => id === componentID);
      const component = components[componentIndex];
      const messageKey = component.teamsMessage ? 'teamsMessage' : component.slackMessage ? 'slackMessage' : component.pushMessage ? 'pushMessage' : 'emailMessage';
      const daysToShift = component[messageKey].relativeSendAt.days;
      const nextComponentMessageKey = components[componentIndex + 1] ? (components[componentIndex + 1].teamsMessage
        ? 'teamsMessage' : components[componentIndex + 1].slackMessage
          ? 'slackMessage' : components[componentIndex + 1].pushMessage
            ? 'pushMessage' : 'emailMessage') : null;
      if (nextComponentMessageKey && !moveComponents) {
        components[componentIndex + 1][nextComponentMessageKey].relativeSendAt.days += daysToShift;
      }
      if (nextComponentMessageKey && components[componentIndex - 1]) {
        components[componentIndex + 1][nextComponentMessageKey].relativeSendAt.componentID = components[componentIndex - 1].id;
      }
      if (nextComponentMessageKey && !components[componentIndex - 1]) {
        components[componentIndex + 1][nextComponentMessageKey].relativeSendAt.componentID = null;
      }
      await this.updateComponentDates(components.filter(({ id }) => id !== componentID));
    },
    async updateComponentDays(componentID) {
      this.$bvModal.hide(`edit-days-modal-${componentID}`);
      const { editDaysForm, orderedComponents } = this;
      const components = JSON.parse(JSON.stringify(orderedComponents));
      const { days: daysString, moveComponentsBack } = editDaysForm;
      const days = parseInt(daysString);
      const componentIndex = components.findIndex(({ id }) => id === componentID);
      const component = components[componentIndex];
      const messageKey = component.teamsMessage ? 'teamsMessage' : component.slackMessage ? 'slackMessage' : component.pushMessage ? 'pushMessage' : 'emailMessage';
      const originalDays = component[messageKey].relativeSendAt.days;
      const daysToShift = days - originalDays;
      components[componentIndex][messageKey].relativeSendAt.days = days;
      if (components[componentIndex + 1] && !moveComponentsBack) {
        const nextComponentMessageKey = components[componentIndex + 1].teamsMessage
          ? 'teamsMessage' : components[componentIndex + 1].slackMessage
            ? 'slackMessage' : components[componentIndex + 1].pushMessage
              ? 'pushMessage' : 'emailMessage';
        if (components[componentIndex + 1][nextComponentMessageKey].relativeSendAt.days >= daysToShift) {
          components[componentIndex + 1][nextComponentMessageKey].relativeSendAt.days -= daysToShift;
        } else {
          components[componentIndex + 1][nextComponentMessageKey].relativeSendAt.days = 0;
        }
      }
      await this.updateComponentDates(components);
    },
    async updateComponentDates(components) {
      this.areDatesUpdating = true;
      const oldComponents = JSON.parse(JSON.stringify(this.orderedComponents));
      const newComponents = JSON.parse(JSON.stringify(components));
      const campaignStartDate = this.campaign.startDate instanceof Date ? new Date(this.campaign.startDate.getTime()) : new Date(this.campaign.startDate);
      campaignStartDate.setHours(0);
      campaignStartDate.setMinutes(0);

      const newComponentDates = [];
      newComponents.forEach(({ pushMessage, teamsMessage, slackMessage, emailMessage, id }, index) => {
        const message = teamsMessage || slackMessage || emailMessage || pushMessage || {};
        const { relativeSendAt } = message || {};
        const oneDay = 1000 * 60 * 60 * 24;
        delete message.__typename;
        delete relativeSendAt.__typename;
        if (message && message.job) {
          delete message.job.__typename;
          if (message.job.image) {
            delete message.job.image.__typename;
          }
        }
        if (message && Array.isArray(message.jobs)) {
          message.jobs.forEach(j => {
            delete j.__typename;
            if (j.image) {
              delete j.image.__typename;
            }
          });
        }
        if (message && message.contentShare) {
          delete message.contentShare.__typename;
          if (message.contentShare.image) {
            delete message.contentShare.image.__typename;
          }
        }
        if (message && Array.isArray(message.contentShares)) {
          message.contentShares.forEach(c => {
            delete c.__typename;
            if (c.image) {
              delete c.image.__typename;
            }
          });
        }
        if (index === 0) {
          newComponentDates.push({
            id,
            data: {
              [teamsMessage ? 'teamsMessage' : slackMessage ? 'slackMessage' : pushMessage ? 'pushMessage' : 'emailMessage']: {
                ...message,
                relativeSendAt: {
                  ...relativeSendAt,
                  componentID: null,
                },
                sendAt: new Date(campaignStartDate.getTime() + (relativeSendAt.days * oneDay) + (relativeSendAt.atHour * 60 * 60 * 1000) + (relativeSendAt.atMinute * 60 * 1000)),
              }
            },
          });
        } else {
          const prevComponent = newComponentDates[index - 1];
          const prevComponentID = prevComponent.id;
          const prevMessage = prevComponent.data.teamsMessage || prevComponent.data.slackMessage || prevComponent.data.emailMessage || prevComponent.data.pushMessage;
          const prevSendAt = prevMessage.sendAt instanceof Date ? new Date(prevMessage.sendAt.getTime()) : new Date(prevMessage.sendAt);
          prevSendAt.setHours(0);
          prevSendAt.setMinutes(0);
          newComponentDates.push({
            id,
            data: {
              [teamsMessage ? 'teamsMessage' : slackMessage ? 'slackMessage' : pushMessage ? 'pushMessage' : 'emailMessage']: {
                ...message,
                relativeSendAt: {
                  ...relativeSendAt,
                  componentID: prevComponentID,
                },
                sendAt: new Date(prevSendAt.getTime() + (relativeSendAt.days * oneDay) + (relativeSendAt.atHour * 60 * 60 * 1000) + (relativeSendAt.atMinute * 60 * 1000)),
              }
            },
          });
        }
      });

      const updates = newComponentDates.filter(({ id, data }) => {
        const oldComponent = oldComponents.find(({ id: componentID }) => id === componentID);
        if (!oldComponent) return true;
        const oldMessage = oldComponent.teamsMessage || oldComponent.slackMessage || oldComponent.emailMessage || oldComponent.pushMessage;
        const newMessage = data.teamsMessage || data.slackMessage || data.emailMessage || data.pushMessage;
        newMessage.sendAt = newMessage.sendAt.getTime();
        return !_isEqual(oldMessage, newMessage);
      });

      try {
        if (updates && updates.length) {
          await Promise.all(updates.map(update => this.$apollo.mutate({
            mutation: UpdateCampaignComponentMutation,
            variables: {
              campaign: { id: this.campaignID },
              componentID: update.id,
              update: update.data,
            },
          })));
        }
        const activeComponentID = this.activeComponent ? this.activeComponent.id : null;
        this.activeComponent = null;
        if (activeComponentID) {
          setTimeout(() => {
            this.activeComponent = this.orderedComponents.find(({ id }) => id === activeComponentID);
          }, 100);
        }
        this.$notification({ message: 'Messages have been successfully updated', variant: 'success' });
      } catch (error) {
        console.log(error);
        this.$notifyErr(error, { prepend: error.message || 'Failed to update message dates' });
      }
      this.areDatesUpdating = false;
    }
  },
};
</script>

<style lang="scss">
.stages-editor {
  .left-side {
    position: absolute;
    top: 0px;
    bottom: 0px;
    left: 0px;
    width: 280px;
  }
  .right-side {
    position: absolute;
    top: 0px;
    bottom: 0px;
    right: 0px;
    left: 280px;
    overflow-y: scroll;
  }
  .start-box {
    position: absolute;
    top: 0px;
    right: 0px;
    left: 0px;
    height: 40px;
    background: #f8fafc;
  }
  .end-box {
    position: absolute;
    bottom: 0px;
    right: 0px;
    left: 0px;
    height: 40px;
    background: #f8fafc;
  }
  .stages-box {
    position: absolute;
    top: 40px;
    bottom: 40px;
    right: 0px;
    left: 0px;
    overflow-y: scroll;
  }
  .active-on-hover:hover,
  .active-on-hover:active {
    background: #f1f5f9;
  }
  .component-icon {
    display: inline-block;
    margin-right: 10px;
    font-size: 10px;
    width: 24px;
    height: 24px;
    line-height: 25px;
    text-align: center;
    border-radius: 50%;
    color: #fff;
  }
  .vertical-line {
    width: 0px;
    position: absolute;
    top: 54px;
    bottom: 54px;
    z-index: 0;
  }
  .plus-button {
    display: block;
    width: 36px;
    line-height: 36px;
    text-align: center;
    color: #fff;
    border-radius: 18px;
    z-index: 1;
    cursor: pointer;

    &:hover {
      opacity: 0.9;
    }

    &:active {
      opacity: 0.95;
    }
  }
  .scrollable-box-loading {
    z-index: 10;
    position: absolute;
    top: 0px;
    bottom: 0px;
    right: 0px;
    left: 0px;
    background: rgba(255, 255, 255, 0.5);
  }
}
</style>



// WEBPACK FOOTER //
// src/views/campaign-admin/EngagementRelativeDates.vue